/**
 * @fileoverview Authentication actions and hooks for user authentication, session management, and password reset
 * @module AuthActions
 */

import { Auth } from "aws-amplify";
import apiClient from "../utils/apiClient";
import useSWR from "swr";
import axios from "axios";

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */

/**
 * Returns the plan for the current user client
 * @function
 * @name usePlan
 * @memberof module:AuthActions
 * @returns {SWRObject} SWR Object containing plan data
 */

export const usePlan = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error } = useSWR("/clients/plan/", fetcher);

  return { data, isLoading: !error && !data, error };
};

/**
 * Authenticates a user with username and password
 * @function
 * @name authUser
 * @memberof module:AuthActions
 * @param {string} username - Email for the user
 * @param {string} password - Password for the user
 * @returns {Promise<Object>} Authentication result from AWS Amplify
 */
export const authUser = async (username, password) =>
  Auth.signIn(username, password);

/**
 * Gets the current AWS Amplify authentication session
 * @function
 * @name getSession
 * @memberof module:AuthActions
 * @returns {Promise<Object>} The current authentication session
 */
export const getSession = () => Auth.currentSession();

/**
 * Hook that returns the session for the current user
 * @function
 * @name useSession
 * @memberof module:AuthActions
 * @returns {SWRObject} SWR Object containing session data
 * @example
 * const { data, isLoading, error } = useSession();
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Failed to load</div>;
 * if (data) return <div>Session: {data}</div>;
 */

export const useSession = () => {
  const fetcher = () => Auth.currentSession();
  const { data, error } = useSWR("/session/", fetcher);

  return { data, isLoading: !error && !data, error };
};

/**
 * Sends a recovery code to reset password
 * @function
 * @name sendResetPasswordCode
 * @memberof module:AuthActions
 * @param {Object} data - Object containing username (email)
 * @returns {Promise<Object>} API response
 */
export async function sendResetPasswordCode(data) {
  return axios.post(
    `${import.meta.env.VITE_API_URL}auth/send_code_password/`,
    data
  );
}

/**
 * Sends the recovery code and required data to reset password
 * @function
 * @name sendResetPassword
 * @memberof module:AuthActions
 * @param {Object} data - Object containing username, code and password
 * @returns {Promise<Object>} API response
 */
export async function sendResetPassword(data) {
  return axios.post(
    `${import.meta.env.VITE_API_URL}auth/reset_password/`,
    data
  );
}

/**
 * Signs out the current user from AWS Amplify authentication
 * @function
 * @name signOut
 * @memberof module:AuthActions
 * @returns {Promise<void>} Promise that resolves when sign out is complete
 */
export async function signOut() {
  await Auth.signOut();
}
